/**
 * The photo carousel on a post.
 *
 * Built on native scroll-snap rather than a pointer-event gesture layer. The
 * viewer needs custom handling because it also owns pinch and pan; a feed
 * carousel does not, and the native implementation already gives momentum,
 * rubber-banding, trackpad support, keyboard scrolling and accessibility for
 * free — all of which a hand-rolled version would have to re-earn.
 */

import { useCallback, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useDecryptedUrl, type MediaRef } from "../../lib/useDecryptedUrl.js";
import "./PostMedia.css";

export interface PostMediaProps {
  fileIds: string[];
  sources: Record<string, MediaRef>;
  groupId: string;
  onOpen: (fileId: string) => void;
}

export function PostMedia({
  fileIds,
  sources,
  groupId,
  onOpen,
}: PostMediaProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  /*
   * Which photo is centred, derived from scroll position.
   *
   * Rounding the offset by slide width is enough — an IntersectionObserver per
   * slide would be more machinery for the same answer, and this only has to be
   * right at rest, when the snap has finished.
   */
  const onScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const width = track.clientWidth;
    if (width === 0) return;
    const next = Math.round(track.scrollLeft / width);
    setActive((current) => (current === next ? current : next));
  }, []);

  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: index * track.clientWidth, behavior: "smooth" });
  };

  const single = fileIds.length === 1;

  /*
   * One shape for the whole post, taken from its first photograph.
   *
   * Per-slide ratios would change the card's height mid-swipe, which is the
   * one thing a carousel must never do. The first picture decides, the rest
   * fill it, and the bounds keep a panorama from becoming a slit and a tall
   * screenshot from taking three screens to pass.
   */
  const lead = sources[fileIds[0] ?? ""];
  const ratio =
    lead?.width && lead.height
      ? Math.min(16 / 9, Math.max(9 / 16, lead.width / lead.height))
      : 4 / 5;

  return (
    <div
      className="fc-postmedia"
      data-single={single || undefined}
      style={{ "--fc-post-ratio": String(ratio) } as CSSProperties}
    >
      <div
        className="fc-postmedia__track"
        ref={trackRef}
        onScroll={onScroll}
        role={single ? undefined : "group"}
        aria-label={single ? undefined : `${String(fileIds.length)} photos`}
      >
        {fileIds.map((fileId, position) => (
          <button
            key={fileId}
            type="button"
            className="fc-postmedia__slide"
            onClick={() => onOpen(fileId)}
            aria-label={`Open photo ${String(position + 1)} of ${String(fileIds.length)}`}
          >
            <Slide source={sources[fileId]} groupId={groupId} />
          </button>
        ))}
      </div>

      {!single && (
        <>
          <span className="fc-postmedia__counter fc-tabular" aria-hidden="true">
            {active + 1}/{fileIds.length}
          </span>

          {/* Dots are a control on a pointer device and a status readout on a
              phone, where the swipe is the real affordance. */}
          <div className="fc-postmedia__dots">
            {fileIds.map((fileId, position) => (
              <button
                key={fileId}
                type="button"
                className="fc-postmedia__dot"
                data-active={position === active || undefined}
                aria-label={`Go to photo ${String(position + 1)}`}
                aria-current={position === active}
                onClick={() => goTo(position)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * One picture in the carousel.
 *
 * A component of its own because decrypting needs a hook, and hooks cannot run
 * inside the loop that lays the slides out. The lookahead the old markup got
 * from `loading="eager"` on the first two slides is gone with it: the browser
 * is no longer the one fetching, so the attribute has nothing to act on. Every
 * slide in a post starts at once instead, which for the two-to-five pictures a
 * post carries is the same thing.
 */
function Slide({
  source,
  groupId,
}: {
  source: MediaRef | undefined;
  groupId: string;
}) {
  const { url } = useDecryptedUrl(source, groupId);

  if (!url) return <span className="fc-postmedia__blank" aria-hidden="true" />;

  return <img src={url} alt="" decoding="async" draggable={false} />;
}
